import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { Title } from '@angular/platform-browser';
import { SubscriptionService } from "../../../shared/services/subscription.service";
import { AuthService } from "../../../shared/services/auth.service";
import { PlanCard } from 'src/app/shared/components/plan-card/plan-card.model';
import { properties } from 'src/environments/properties';
import { _windowUtil, convertCurrencyToWords } from "src/app/shared/utils/ui-utils";

@Component({
  selector: 'app-change-plan-summary',
  templateUrl: './change-plan-summary.component.html',
  styleUrls: ['./change-plan-summary.component.scss'],
  providers: [SubscriptionService]
})
export class ChangePlanSummaryComponent implements OnInit {

  updatePlan: boolean;
  userInfo: any;
  annualPlan: any;
  monthlyPlan: any;
  changeCode: any;
  fetchedPlans: any;
  userSubscriptionDetails: any;
  showLoader = true;
  tenderInfo: any;
  userEnrollmentDetails: any;
  termsID: any;
  authUserInfo: any;
  showPlanTypes = true;
  currentEnvironment;
  properties = properties;
  isRedirectedFromPrechurn = false;
  referrerPath: string = '';

  retentionPlanDiscount: any;
  isB2B: boolean;
  isRedirectedFromSubscribePage: boolean = false;

  public windowUtil = _windowUtil(window);
  convertCurrencyToWords = convertCurrencyToWords;

  combinedPlans = [];

  enableSnap = this.properties.featureFlags.enableSnap;
  enableForUPlus: boolean = this.properties.featureFlags.enableForUPlus;
  programDisplayData = this.enableForUPlus ? this.properties.programType.forUPlus : this.properties.programType.freshpass;

  constructor(
    public subscriptionService: SubscriptionService,
    public authService: AuthService,
    public activeRoute: ActivatedRoute,
    private router: Router,
    private titleService: Title
  ) {
    this.isB2B = this.authService.checkB2B();
    this.updatePlan = false;
    this.processRedirectionRoutingLogic();
  }

  ngOnInit(): void {
    this.setTitle(`${this.programDisplayData.title} - Change Plan`);
    this.currentEnvironment = this.subscriptionService.getEnvironment();
    if (this.activeRoute.data) {
      this.activeRoute.data.subscribe({
        next: (data) => {
          let isSessionValid = this.authService.isTokenValidForSession(data.tokenObject);
          if (isSessionValid) {
            const sessionData = { ...(data.tokenObject), authToken: data.tokenObject['SWY_SHOP_TOKEN'] };
            if (sessionData) {
              this.userInfo = sessionData;
              if (this.isRedirectedFromSubscribePage && !this.userSubscriptionDetails) {
                this.getSubscriptionDetails();
              } else {
                this.fetchDeliverySubscriptionTenders();
              }
            }
          } else {
            this.authService.redirectToSSO(data.tokenObject);
          }
        },
        error: (err) => {
          this.authService.redirectToSSO({});
        }
      });
    }
  }

  processRedirectionRoutingLogic() {
    //read path from url to determine if user is redirected from /freshpass/manage/subscribe and document referrer path is from expedia or partners
    if (this.checkIfRedirectedFromSubscribePageWithExpedia()) {
      this.isRedirectedFromSubscribePage = true;
    } else {
      const routerParams = this.router?.getCurrentNavigation().extras.state;
      if (routerParams?.isRedirectedFromManageLink && routerParams?.userSubscriptionDetails) {
        this.userSubscriptionDetails = routerParams.userSubscriptionDetails;
      } else if (routerParams?.isRedirectedFromPrechurnDiscount && routerParams?.userSubscriptionDetails && routerParams?.retentionPlanDiscount) {
        this.isRedirectedFromPrechurn = true;
        this.updatePlan = true
        this.showPlanTypes = false;
        this.userSubscriptionDetails = routerParams?.userSubscriptionDetails;
        this.retentionPlanDiscount = routerParams.retentionPlanDiscount;
      }
      else {
        this.router.navigate([this.programDisplayData?.urlPath?.MANAGE_PLAN_PATH]);
      }
    }
  }

  fetchPlans() {
    this.showLoader = true;
    this.subscriptionService.getPlans(this.userInfo, this.currentEnvironment).subscribe((data) => {
      this.showLoader = false;
  
      if (data && data.Data) {
        this.fetchedPlans = data;
        this.monthlyPlan = data.Data.subscriptions[1];
        this.annualPlan = data.Data.subscriptions[0];
        if (this.enableSnap) {
          this.preparePlanCards(data);
        }
      }
    },
      (err => {
        this.showLoader = false;
      }));
  }

  fetchDeliverySubscriptionTenders() {
    this.subscriptionService.getTenders(this.userInfo, this.currentEnvironment).subscribe((tenderData) => {
      if (tenderData && tenderData.paymentMethods && tenderData.paymentMethods.length > 0) {
        tenderData.paymentMethods.forEach((tenderObj) => {
          if (tenderObj.primaryPurposes.includes("DELIVERY_SUBSCRIPTION")) {
            this.tenderInfo = tenderObj;
            //Prechurn flow
            if (this.isRedirectedFromPrechurn && this.retentionPlanDiscount) {
              this.changePlan(this.retentionPlanDiscount.code)
            } else {
              //Standard change plan flow
              this.fetchPlans();
            }
          }
        });
      }
    });
  }

  goToUpdate() {
    this.updatePlan = true;
  }

  changePlan(code: any, plan?: any) {
    if (!code) {
      this.windowUtil.setWindowLocationHref(
        this.programDisplayData?.urlPath?.GOV_ASSIST_PATH
      );
    }
    else {
      this.showLoader = true;
      this.changeCode = code;
      this.subscriptionService.getTerms(this.userInfo, this.currentEnvironment).subscribe((data) => {
        this.showLoader = false;
        if (data) {
          this.termsID = data.id
          this.userEnrollmentDetails = {
            gid: this.userInfo.customerId
          };
          this.showPlanTypes = false;
        }
      },
        (err => {
          this.showLoader = false;
        }));
      this.updatePlan = true;
    }
  }

  setTitle(newTitle: string): void {
    this.titleService.setTitle(newTitle)
  }


  preparePlanCards(data) {
    const subscriptions = data.Data.subscriptions || [];

    const promotionPlans = this.enableSnap && data.Data.promotionPlans  && !this.isB2B
      ? data.Data.promotionPlans.filter(plan => plan.planName === this.programDisplayData?.planCard?.SNAP) : [];

    const combinedPlansArray = [
      ...subscriptions.map(plan => this.mapCardPlans(plan, plan.caption, plan.isSheerVerified)),
      ...promotionPlans.map(plan => this.mapCardPlans(plan, plan.planName, false))
    ];

    // Sort the array, placing objects with selected: true first
    combinedPlansArray.sort((a: any, b: any) => b.details.selected - a.details.selected);
    this.combinedPlans = combinedPlansArray;
  }

  public mapCardPlans(plan: any, key: string, isSheerVerified): PlanCard {

    const annualPlanCard: PlanCard = {
      title: key,
      action: this.programDisplayData?.planCard?.selectAction,
      banner: plan.pricingDetailLine1,
      bannerAnouncement: convertCurrencyToWords(plan.pricingDetailLine1),
      subBanner: this.programDisplayData?.planCard?.annualPlanDescription,
      subBannerAnouncement: "",
      details: plan
    };

    const monthlyPlanCard: PlanCard = {
      title: key,
      action: this.programDisplayData?.planCard?.selectAction,
      banner: plan.pricingDetailLine1,
      bannerAnouncement: convertCurrencyToWords(plan.pricingDetailLine1),
      subBanner: this.programDisplayData?.planCard?.monthlyPlanDescription,
      subBannerAnouncement: "",
      details: plan
    };

    const snapPlanCard: PlanCard = {
      title: "",
      action: "",
      banner: plan.discountText,
      subBanner: this.programDisplayData?.planCard?.SNAPPlanDescription,
      details: plan
    };

    if (key === this.programDisplayData?.planCard?.AnnualPlan) {
      if (isSheerVerified) {
        annualPlanCard.banner = `$${plan.planPrice} <s class="strick">$${plan.regularPlanPrice}</s> ${plan.planPriceDetail}`;
        annualPlanCard.bannerAnouncement = convertCurrencyToWords(plan.planPrice);
        annualPlanCard.subBanner = this.programDisplayData?.planCard?.planDescriptionForSNAPVerified;
        annualPlanCard.subBannerAnouncement = convertCurrencyToWords(annualPlanCard.subBanner);
        if (this.userSubscriptionDetails.promoInfo && plan.selected) {

          // scenario where user becomes snap verified and is not fully switched to snap discounted rate. pending fee is the same as plan price we display regular
          if (!this.userSubscriptionDetails.pendingFee || ((parseFloat(this.userSubscriptionDetails.pendingFee).toString() === plan.planPrice) && (this.userSubscriptionDetails.pendingFee !== this.userSubscriptionDetails.fee))) {
            annualPlanCard.banner = `$${plan.regularPlanPrice}${plan.planPriceDetail}`;
            annualPlanCard.bannerAnouncement= convertCurrencyToWords(annualPlanCard.banner);
            annualPlanCard.subBanner = this.programDisplayData?.planCard?.annualPlanDescription
                    .replace("{price}", this.userSubscriptionDetails.pricingDetailLine2.replace(/[\(\)']+/g, ""))
                    .replace("<br/>", ". ")
                    .replace("{renewPrice}", ` at $${plan.pricingDetailLine1.replace(/[\(\)']+/g, "")}`);
            annualPlanCard.subBannerAnouncement = convertCurrencyToWords(annualPlanCard.subBanner);
          } else if (this.userSubscriptionDetails?.fee && this.userSubscriptionDetails?.pricingDetail){
            // handles regular snap scenario with promoInfo and plan selected
            annualPlanCard.banner = `$${this.userSubscriptionDetails.fee} ${this.userSubscriptionDetails.pricingDetail}`;
            annualPlanCard.bannerAnouncement= convertCurrencyToWords(annualPlanCard.banner);
            annualPlanCard.subBanner = this.programDisplayData?.planCard?.annualPlanDescription
                    .replace("{price}", this.userSubscriptionDetails.pricingDetailLine2.replace(/[\(\)']+/g, ""))
                    .replace("<br/>", ". ")
                    .replace("{renewPrice}", ` at $${plan.pricingDetailLine1.replace(/[\(\)']+/g, "")}`);
            annualPlanCard.subBannerAnouncement = convertCurrencyToWords(annualPlanCard.subBanner);
          }
          annualPlanCard.benefits = [
            {
              icon: this.programDisplayData?.planCard?.checkmarkCircle,
              label: this.userSubscriptionDetails.promoInfo,
              textColor: this.programDisplayData?.planCard?.successColor
            }
          ];
          if (!this.enableForUPlus) {
            annualPlanCard.benefits.push({
              icon: "ocsp-web-ui/dist/assets/images/Union.svg",
              label: this.programDisplayData?.planCard?.annualCreditLabel
            });
          }
        }
      }
      else {
        if (plan.isDiscountedPrice && plan.discountDuration) {
          annualPlanCard.subBanner = this.programDisplayData?.planCard?.annualDiscountPlanDescription?.replace("{renewPrice}", " at $" + plan.regularPlanPrice.replace(/[\(\)']+/g, "") + "/year");
          annualPlanCard.subBannerAnouncement = convertCurrencyToWords(annualPlanCard.subBanner);
        } else {
          //regular scenario: current user has pending plan: show current pricing and renewal pricing to pending plan
          if (this.userSubscriptionDetails?.pendingFee && this.userSubscriptionDetails?.pendingPricingDetail) {
              const pricingDetail = this.subscriptionService.getPricingDetail(this.userSubscriptionDetails.pricingDetail, this.userSubscriptionDetails.subscriptionType);
              const pendingPricingDetail = this.subscriptionService.getPricingDetail(this.userSubscriptionDetails.pendingPricingDetail, this.userSubscriptionDetails.pendingSubscriptionType);
              annualPlanCard.banner = `$${this.userSubscriptionDetails?.fee}${pricingDetail}`;
              annualPlanCard.bannerAnouncement = convertCurrencyToWords(annualPlanCard.banner);
              annualPlanCard.subBanner = this.programDisplayData?.planCard?.annualPlanDescription
                      .replace("{price}", this.userSubscriptionDetails.pricingDetailLine2.replace(/[\(\)']+/g, ""))
                      .replace("<br/>", ". ")
                      .replace("{renewPrice}", ` at $${this.userSubscriptionDetails.pendingFee}${pendingPricingDetail}`);
              annualPlanCard.subBannerAnouncement = convertCurrencyToWords(annualPlanCard.subBanner);
          } else {
              annualPlanCard.subBanner = annualPlanCard.subBanner
                .replace("{price}", plan.pricingDetailLine2.replace(/[\(\)']+/g, ""))
                .replace("<br/>", ". ")
                .replace("{renewPrice}", ` at $${plan.pricingDetailLine1.replace(/[\(\)']+/g, "")}`);
              annualPlanCard.subBannerAnouncement = convertCurrencyToWords(annualPlanCard.subBanner);
           }
        }
      }
      return annualPlanCard;
    }
    else if (key === this.programDisplayData?.planCard?.MonthlyPlan) {
      if (isSheerVerified) {
        monthlyPlanCard.banner = `$${plan.planPrice} ${plan.isSheerVerified ? `<s class="strick">$${plan.regularPlanPrice}</s>` : ''} ${plan.planPriceDetail}`;
        monthlyPlanCard.bannerAnouncement = convertCurrencyToWords(monthlyPlanCard.banner);
        monthlyPlanCard.subBanner = this.programDisplayData?.planCard?.monthlyDescriptionForSNAPVerifiedRef?.replace("{price}", plan.pricingDetailLine1.replace(/[\(\)']+/g, ""));
        monthlyPlanCard.subBannerAnouncement = convertCurrencyToWords(monthlyPlanCard.subBanner);

        if (this.userSubscriptionDetails.promoInfo && plan.selected) {
          monthlyPlanCard.banner = `$${plan.regularPlanPrice}${plan.planPriceDetail}`;
          monthlyPlanCard.bannerAnouncement = convertCurrencyToWords(monthlyPlanCard.banner);
          monthlyPlanCard.benefits = [
            {
              icon: this.programDisplayData?.planCard?.checkmarkCircle,
              label: this.userSubscriptionDetails.promoInfo,
              textColor: this.programDisplayData?.planCard?.successColor
            }
          ];
        }
      }
      else {
        if (plan?.isDiscountedPrice && plan?.discountDuration) {
          let monthlyPrice = ` at $${plan?.regularPlanPrice}/mo after ${plan?.discountDuration} months`;
          monthlyPlanCard.subBanner = monthlyPlanCard.subBanner ? monthlyPlanCard.subBanner.replace("{price}", monthlyPrice) : "";
          monthlyPlanCard.subBannerAnouncement = convertCurrencyToWords(monthlyPlanCard.subBanner);
        } else {
          monthlyPlanCard.subBanner = monthlyPlanCard.subBanner ? monthlyPlanCard.subBanner.replace("{price}", " at $" + plan.pricingDetailLine1.replace(/[\(\)']+/g, "")) : "";
          monthlyPlanCard.subBannerAnouncement = convertCurrencyToWords(monthlyPlanCard.subBanner);
        }
      }
      return monthlyPlanCard;
    }
    else if (key === this.programDisplayData?.planCard?.SNAP) {

      //Backend api is not providing' Plans' text, we need to append if 'Gov assisted' is coming
      if (plan?.planDetails?.title?.includes('Gov assisted')) {
        snapPlanCard.title = plan.planDetails.title + ' Plans';
      } else {
        snapPlanCard.title = plan.planDetails.title;
      }

      snapPlanCard.action = plan.planDetails.verificationDisplayText;
      snapPlanCard.banner = plan.discountText ? plan.discountText : this.programDisplayData?.changePlanSummary?.offYourSubscription;
      return snapPlanCard;
    }
  }

  getSubscriptionDetails() {
    this.subscriptionService.getUserSubscriptionDetails(this.userInfo, this.currentEnvironment).subscribe({
      next: (data) => {
        if (data?.Data) {
          this.userSubscriptionDetails = data.Data;
          this.redirectToManageIfChangePlanNotAllowed();
        }
      },
      error: (error) => {
        console.error('Error retrieving subscription details:', error);
      }
    });
  }

  checkIfRedirectedFromSubscribePageWithExpedia = (queryString = window.location.search) => {
    const urlParams = new URLSearchParams(queryString);
    return urlParams.get('webSource') === this.programDisplayData?.WEBSOURCE?.EXPEDIA && !!urlParams.get('referrer'); 
  }

  redirectToManageIfChangePlanNotAllowed() {
    if (!this.userSubscriptionDetails?.isPlanChangeAvailable) {
      this.router.navigate([this.programDisplayData?.urlPath?.MANAGE_PLAN_PATH]);
    } else {
      this.fetchDeliverySubscriptionTenders();
    }
  }
}
